import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { message } from 'antd';
import React from 'react';
import classNames from 'classnames';
import styles from './copy-to-clipboard.module.scss';
import { useLiveRegion } from 'src/hooks/accessibility';
import { Tooltip } from '../../core/tooltip/tooltip.component';

export type CopyToClipboardProps = {
  children: React.ReactNode;
  text: string;
  onCopy?: () => void;
  showCopiedFeedback?: boolean;
  className?: string;
  /** Accessible name for icon-only usage: sets aria-label and adds a tooltip. */
  label?: string;
};

export function CopyToClipboard({
  children,
  text,
  onCopy = () => {},
  showCopiedFeedback = true,
  className,
  label,
}: CopyToClipboardProps) {
  const lang = useSelector(getLang('SHARE'));
  const [messageApi, contextHolder] = message.useMessage();
  const { announce, liveRegion } = useLiveRegion();

  const copiedFeedback = () => {
    if (showCopiedFeedback) {
      messageApi.success(lang.COPIED);
    }
    announce(lang.COPIED);
  };

  const copyText = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!navigator.clipboard) {
      messageApi.error(lang.COPY_FAILED);
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(copiedFeedback)
      .then(onCopy)
      .catch(() => {
        messageApi.error(lang.COPY_FAILED);
      });
  };

  const button = (
    <button
      type="button"
      onClick={copyText}
      data-copy-to-clipboard
      aria-label={label}
      className={classNames(styles.copyToClipboard, className)}>
      {children}
    </button>
  );

  return (
    <>
      {contextHolder}
      {liveRegion}
      {label ? (
        <Tooltip title={label} announceOnOpen={false}>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </>
  );
}
